import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import surveyApiService from '../../../services/surveyApi';
import { useSurveyResponseState } from '../../student/surveys/useSurveyResponseState';

// The magic-link counterpart to useSurveyPlayer — same answer-state/branching
// logic (useSurveyResponseState), but loads/submits by token via the public
// (unauthenticated) API instead of by assignmentId + login session.
export function usePublicSurveyPlayer() {
  const { token } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await surveyApiService.getPublicSurveyByToken(token);
        if (!res.success) {
          setError(res.message || 'This survey link is invalid or has expired');
          return;
        }
        setData(res.data);
      } catch (err) {
        setError(err.message || 'This survey link is invalid or has expired');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const responseState = useSurveyResponseState(data);

  const handleSubmit = async () => {
    const missing = responseState.getMissingRequired();
    if (missing.length > 0) {
      const firstMissingIndex = responseState.visibleQuestions.findIndex(q => q.id === missing[0].id);
      if (firstMissingIndex !== -1) responseState.setCurrentQuestionIndex(firstMissingIndex);
      setError(`Please answer all required questions: ${missing.map(q => q.prompt).join('; ')}`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await surveyApiService.submitPublicSurveyResponses(token, responseState.buildResponsesPayload());
      if (res.success) {
        setJustSubmitted(true);
      } else {
        setError(res.message || 'Failed to submit survey');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  return { ...responseState, data, error, handleSubmit, justSubmitted, loading, submitting };
}
